/**
 * Visit Clinic (施術入力 / 来院) API service.
 * A2/R2 service.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import type {
  VisitClinicScheduleData,
  VisitClinicStorePayload,
} from '@/types/visitClinic.types';

export interface YearmonthSearchResponse {
  yearmonth: string; // "YYYY-MM"
}

/**
 * 施術入力: 受診者の最終施術年月を取得する.
 * A2 VisitClinicController::searchYearmonth と同じロジック.
 *
 * GET /visit-clinic/yearmonth-search/{patientId}
 * Returns { yearmonth: "YYYY-MM" }
 */
export async function getYearmonthSearch(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  serviceType?: string
): Promise<YearmonthSearchResponse> {
  const response = await apiClient.get<{ success: boolean; data: YearmonthSearchResponse }>(
    clinicApiPath(siteType, clinicNumber, `/visit-clinic/yearmonth-search/${patientId}`),
    serviceType ? { headers: { 'X-Service-Type': serviceType } } : undefined
  );
  return response.data.data;
}

/**
 * 施術入力カレンダー: 受診者の月別施術データを取得する.
 *
 * GET /visit-clinic/{patientId}/{yearmonth}
 * Returns VisitClinicScheduleData
 */
export async function getVisitClinicSchedule(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  yearmonth: string,
  serviceType?: string
): Promise<VisitClinicScheduleData> {
  const response = await apiClient.get<{ success: boolean; data: VisitClinicScheduleData }>(
    clinicApiPath(siteType, clinicNumber, `/visit-clinic/${patientId}/${yearmonth}`),
    serviceType ? { headers: { 'X-Service-Type': serviceType } } : undefined
  );
  return response.data.data;
}

/**
 * 施術入力カレンダー: 月別施術データを保存する.
 *
 * POST /visit-clinic/{patientId}/{yearmonth}
 */
export async function storeVisitClinicSchedule(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  yearmonth: string,
  payload: VisitClinicStorePayload,
  serviceType?: string
): Promise<{ saved: boolean; message: string }> {
  const response = await apiClient.post<{ success: boolean; data: { saved: boolean; message: string } }>(
    clinicApiPath(siteType, clinicNumber, `/visit-clinic/${patientId}/${yearmonth}`),
    payload,
    serviceType ? { headers: { 'X-Service-Type': serviceType } } : undefined
  );
  return response.data.data;
}

/**
 * 施術入力カレンダー: 一括削除.
 *
 * DELETE /visit-clinic/{patientId}/{yearmonth}
 */
export async function deleteVisitClinicSchedule(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  yearmonth: string,
  serviceType?: string
): Promise<{ deleted: number; message: string }> {
  const response = await apiClient.delete<{ success: boolean; data: { deleted: number; message: string } }>(
    clinicApiPath(siteType, clinicNumber, `/visit-clinic/${patientId}/${yearmonth}`),
    serviceType ? { headers: { 'X-Service-Type': serviceType } } : undefined
  );
  return response.data.data;
}
